import React, { useEffect, useRef, useState } from "react";
import {
  ScanFace, Plus, MonitorSmartphone, AlertTriangle, Loader2, CheckCircle2,
  Camera, KeyRound, ShieldOff, Trash2, RefreshCw, Copy, Clock,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "../components/ui/alert-dialog";
import { timeApi } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { loadModels, captureEnrolmentDescriptor } from "../lib/face";
import { useToast } from "../hooks/use-toast";

const today = () => new Date().toISOString().slice(0, 10);

function SummaryCard({ label, value, color }) {
  return <div className="bg-white rounded-2xl border border-slate-100 p-4"><div className={`font-display font-extrabold text-2xl ${color}`}>{value}</div><div className="text-xs text-slate-500 mt-0.5">{label}</div></div>;
}

function EnrolDialog({ open, onOpenChange, employee, onDone }) {
  const { toast } = useToast();
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const [ready, setReady] = useState(false);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!open) return;
    let active = true;
    (async () => {
      try {
        await loadModels(); if (active) setReady(true);
        const st = await navigator.mediaDevices.getUserMedia({ video: { facingMode: "user" } });
        streamRef.current = st; if (videoRef.current) { videoRef.current.srcObject = st; await videoRef.current.play().catch(() => {}); }
      } catch (e) { toast({ title: "Camera unavailable", description: "Allow camera access to enrol.", variant: "destructive" }); }
    })();
    return () => { if (streamRef.current) streamRef.current.getTracks().forEach((t) => t.stop()); setReady(false); };
  }, [open]);

  const capture = async () => {
    setBusy(true);
    try {
      const r = await captureEnrolmentDescriptor(videoRef.current, 5);
      if (!r.ok) { toast({ title: "No face detected", description: "Center your face and try again.", variant: "destructive" }); return; }
      await timeApi.enroll(employee.employee_id, r.descriptor);
      toast({ title: "Face enrolled", description: `${employee.name} enrolled successfully.` });
      onDone(); onOpenChange(false);
    } catch (e) { toast({ title: "Enrolment failed", description: e?.response?.data?.detail || "Try again", variant: "destructive" }); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader><DialogTitle className="font-display text-brand-navy">Enrol face — {employee?.name}</DialogTitle><DialogDescription>Only a numeric face template is stored; the image is never uploaded.</DialogDescription></DialogHeader>
        <div className="relative aspect-[4/3] rounded-xl overflow-hidden bg-slate-900 mb-4">
          <video ref={videoRef} muted playsInline className="w-full h-full object-cover scale-x-[-1]" />
          <div className="absolute inset-6 border-2 border-brand-sky/60 rounded-2xl pointer-events-none" />
          {!ready && <div className="absolute inset-0 flex items-center justify-center text-white text-sm"><Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading…</div>}
        </div>
        <Button onClick={capture} disabled={!ready || busy} className="w-full bg-brand-blue hover:bg-brand-bluedark text-white rounded-lg h-11">
          {busy ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Capturing…</> : <><Camera className="w-4 h-4 mr-2" /> Capture & enrol</>}
        </Button>
        <p className="text-xs text-amber-600 mt-2">Liveness detection is basic in this prototype — REQUIRES SPECIALIST PROVIDER for production.</p>
      </DialogContent>
    </Dialog>
  );
}

function PinDialog({ open, onOpenChange, employee, onDone }) {
  const { toast } = useToast();
  const [pin, setPin] = useState("");
  useEffect(() => { if (open) setPin(""); }, [open]);
  const save = async () => {
    try { await timeApi.setPin(employee.employee_id, pin); toast({ title: "PIN set", description: `${employee.name}'s PIN updated.` }); onDone(); onOpenChange(false); }
    catch (e) { toast({ title: "Could not set PIN", description: e?.response?.data?.detail || "4-8 digits", variant: "destructive" }); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-xs">
        <DialogHeader><DialogTitle className="font-display text-brand-navy">Set PIN — {employee?.name}</DialogTitle><DialogDescription>Fallback when face isn't available. Stored hashed.</DialogDescription></DialogHeader>
        <Input value={pin} onChange={(e) => setPin(e.target.value.replace(/\D/g, ""))} maxLength={8} placeholder="4-8 digit PIN" inputMode="numeric" />
        <Button onClick={save} disabled={pin.length < 4} className="w-full bg-brand-blue hover:bg-brand-bluedark text-white rounded-lg h-11 mt-2">Save PIN</Button>
      </DialogContent>
    </Dialog>
  );
}

export default function Time() {
  const { toast } = useToast();
  const { session } = useAuth();
  const orgCode = session?.organization?.portal_code;
  const [bios, setBios] = useState([]);
  const [devices, setDevices] = useState([]);
  const [events, setEvents] = useState([]);
  const [exceptions, setExceptions] = useState([]);
  const [summary, setSummary] = useState({});
  const [date, setDate] = useState(today());
  const [enrol, setEnrol] = useState(null);
  const [pinEmp, setPinEmp] = useState(null);
  const [confirm, setConfirm] = useState(null);
  const [devDialog, setDevDialog] = useState(false);
  const [devForm, setDevForm] = useState({ name: "", location: "", username: "", password: "" });
  const [newCred, setNewCred] = useState(null);

  const loadBios = () => timeApi.biometrics().then(setBios).catch(() => {});
  const loadDevices = () => timeApi.devices().then(setDevices).catch(() => {});
  const loadAttendance = (d) => { timeApi.events(d).then(setEvents).catch(() => {}); timeApi.summary(d).then(setSummary).catch(() => {}); timeApi.exceptions(d).then(setExceptions).catch(() => {}); };
  useEffect(() => { loadBios(); loadDevices(); }, []);
  useEffect(() => { loadAttendance(date); }, [date]);

  const bioAction = async (emp, action) => {
    try { await timeApi.biometricAction(emp.employee_id, action); loadBios(); toast({ title: `Biometric ${action}d` }); }
    catch (e) { toast({ title: "Action failed", variant: "destructive" }); }
    setConfirm(null);
  };

  const createDevice = async (e) => {
    e.preventDefault();
    try { const d = await timeApi.createDevice(devForm); setNewCred({ ...d, password: devForm.password }); setDevForm({ name: "", location: "", username: "", password: "" }); setDevDialog(false); loadDevices(); }
    catch (err) { toast({ title: "Could not create kiosk", description: err?.response?.data?.detail || "Try again", variant: "destructive" }); }
  };

  const statusBadge = (st) => st === "enrolled" ? "bg-emerald-100 text-emerald-700" : st === "disabled" ? "bg-amber-100 text-amber-700" : "bg-slate-100 text-slate-500";
  const kioskLink = `${window.location.origin}/time`;

  return (
    <div className="pt-2 pb-6 animate-fade-up">
      <div className="flex flex-wrap items-start justify-between gap-4 mb-6">
        <div>
          <h1 className="font-display font-extrabold text-2xl sm:text-3xl text-brand-navy flex items-center gap-2"><ScanFace className="w-7 h-7 text-brand-blue" /> HRFlo Time</h1>
          <p className="text-slate-500 mt-1">Facial-recognition attendance — enrol once, look at the camera, done.</p>
        </div>
        <a href={kioskLink} target="_blank" rel="noreferrer"><Button variant="outline" className="rounded-lg h-11 border-slate-300"><MonitorSmartphone className="w-4 h-4 mr-2" /> Open kiosk</Button></a>
      </div>

      <Tabs defaultValue="attendance">
        <TabsList className="mb-4 flex-wrap h-auto">
          <TabsTrigger value="attendance">Attendance</TabsTrigger>
          <TabsTrigger value="enrolment">Face Enrolment</TabsTrigger>
          <TabsTrigger value="devices">Devices</TabsTrigger>
          <TabsTrigger value="exceptions">Exceptions</TabsTrigger>
        </TabsList>

        {/* Attendance */}
        <TabsContent value="attendance">
          <div className="flex items-center gap-2 mb-4"><Clock className="w-4 h-4 text-slate-400" /><input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="bg-white border border-slate-200 rounded-lg px-3 h-10 text-sm outline-none" /></div>
          <div className="grid grid-cols-2 sm:grid-cols-6 gap-3 mb-5">
            <SummaryCard label="Present" value={summary.present || 0} color="text-emerald-600" />
            <SummaryCard label="Checked In" value={summary.checkedIn || 0} color="text-brand-blue" />
            <SummaryCard label="Checked Out" value={summary.checkedOut || 0} color="text-slate-700" />
            <SummaryCard label="Late" value={summary.late || 0} color="text-amber-600" />
            <SummaryCard label="On Leave" value={summary.onLeave || 0} color="text-violet-600" />
            <SummaryCard label="Missing Checkout" value={summary.missingCheckout || 0} color="text-red-500" />
          </div>
          <div className="bg-white rounded-2xl border border-slate-100 overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="text-left text-slate-500 border-b border-slate-100 bg-slate-50/50">
                <th className="py-3 px-5 font-medium">Employee</th><th className="py-3 px-3 font-medium">Event</th>
                <th className="py-3 px-3 font-medium">Time</th><th className="py-3 px-3 font-medium">Device</th>
                <th className="py-3 px-3 font-medium">Method</th>
              </tr></thead>
              <tbody>
                {events.map((e) => (
                  <tr key={e.id} className="border-b border-slate-50 last:border-0">
                    <td className="py-3 px-5 font-semibold text-brand-navy">{e.employee_name}<div className="text-xs text-slate-500 font-normal">{e.department}</div></td>
                    <td className="py-3 px-3"><Badge className={`${e.event_type === "CHECK_IN" ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-600"} hover:bg-current/10`}>{e.event_type === "CHECK_IN" ? "Check-in" : "Check-out"}</Badge></td>
                    <td className="py-3 px-3 text-slate-600">{(e.timestamp || "").slice(11, 16)}</td>
                    <td className="py-3 px-3 text-slate-600">{e.device_name}</td>
                    <td className="py-3 px-3"><span className="text-xs font-medium text-slate-500">{e.authentication_method}</span></td>
                  </tr>
                ))}
                {events.length === 0 && <tr><td colSpan={5} className="py-12 text-center text-slate-400">No attendance events for this date.</td></tr>}
              </tbody>
            </table>
          </div>
        </TabsContent>

        {/* Enrolment */}
        <TabsContent value="enrolment">
          <div className="bg-white rounded-2xl border border-slate-100 overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="text-left text-slate-500 border-b border-slate-100 bg-slate-50/50">
                <th className="py-3 px-5 font-medium">Employee</th><th className="py-3 px-3 font-medium">Biometric</th>
                <th className="py-3 px-3 font-medium">PIN</th><th className="py-3 px-5 font-medium text-right">Actions</th>
              </tr></thead>
              <tbody>
                {bios.map((b) => (
                  <tr key={b.employee_id} className="border-b border-slate-50 last:border-0 hover:bg-brand-mist/40">
                    <td className="py-3 px-5"><div className="flex items-center gap-3">{b.avatar ? <img src={b.avatar} alt="" className="w-9 h-9 rounded-full object-cover" /> : <div className="w-9 h-9 rounded-full bg-brand-light text-brand-blue flex items-center justify-center text-xs font-semibold">{b.name?.[0]}</div>}<div><div className="font-semibold text-brand-navy">{b.name}</div><div className="text-xs text-slate-500">{b.employee_number} · {b.department}</div></div></div></td>
                    <td className="py-3 px-3"><Badge className={`${statusBadge(b.biometric_status)} hover:bg-current/10`}>{b.biometric_status === "not_enrolled" ? "Not enrolled" : b.biometric_status}</Badge></td>
                    <td className="py-3 px-3">{b.pin_set ? <span className="text-emerald-600 text-xs font-medium">Set</span> : <span className="text-slate-400 text-xs">—</span>}</td>
                    <td className="py-3 px-5">
                      <div className="flex items-center justify-end gap-2 flex-wrap">
                        <button onClick={() => setEnrol(b)} className="text-xs font-semibold text-brand-blue hover:text-brand-bluedark flex items-center gap-1"><ScanFace className="w-3.5 h-3.5" />{b.biometric_status === "enrolled" ? "Re-enrol" : "Enrol"}</button>
                        <button onClick={() => setPinEmp(b)} className="text-xs font-medium text-slate-600 hover:text-brand-blue flex items-center gap-1"><KeyRound className="w-3.5 h-3.5" />PIN</button>
                        {b.biometric_status === "enrolled" && <button onClick={() => setConfirm({ emp: b, action: "disable" })} className="text-xs font-medium text-amber-600 hover:text-amber-700 flex items-center gap-1"><ShieldOff className="w-3.5 h-3.5" />Disable</button>}
                        {b.biometric_status === "disabled" && <button onClick={() => bioAction(b, "enable")} className="text-xs font-medium text-emerald-600 hover:text-emerald-700 flex items-center gap-1"><RefreshCw className="w-3.5 h-3.5" />Enable</button>}
                        {b.biometric_status !== "not_enrolled" && <button onClick={() => setConfirm({ emp: b, action: "delete" })} className="text-xs font-medium text-red-500 hover:text-red-600 flex items-center gap-1"><Trash2 className="w-3.5 h-3.5" />Delete</button>}
                      </div>
                    </td>
                  </tr>
                ))}
                {bios.length === 0 && <tr><td colSpan={4} className="py-12 text-center text-slate-400">No active employees.</td></tr>}
              </tbody>
            </table>
          </div>
          <p className="text-xs text-slate-400 mt-3">Administrators never see or download the underlying biometric template. Biometric enrolment REQUIRES LEGAL VERIFICATION for lawful basis/consent in your jurisdiction.</p>
        </TabsContent>

        {/* Devices */}
        <TabsContent value="devices">
          <div className="flex justify-between items-center mb-4">
            <div className="text-sm text-slate-500">Kiosk link: <span className="font-mono text-brand-blue">{kioskLink}</span> <button onClick={() => { navigator.clipboard.writeText(kioskLink); toast({ title: "Kiosk link copied" }); }} className="ml-1 text-brand-blue"><Copy className="w-3.5 h-3.5 inline" /></button></div>
            <Button onClick={() => setDevDialog(true)} className="bg-brand-blue hover:bg-brand-bluedark text-white rounded-lg h-10"><Plus className="w-4 h-4 mr-1" /> Add Kiosk</Button>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {devices.map((d) => (
              <div key={d.id} className="bg-white rounded-2xl border border-slate-100 p-5">
                <div className="flex items-start justify-between">
                  <div className="w-11 h-11 rounded-xl bg-brand-light text-brand-blue flex items-center justify-center"><MonitorSmartphone className="w-5 h-5" /></div>
                  <Badge className={`${d.status === "online" ? "bg-emerald-100 text-emerald-700" : d.status === "disabled" ? "bg-red-100 text-red-700" : "bg-slate-100 text-slate-500"} hover:bg-current/10`}>{d.status}</Badge>
                </div>
                <div className="font-display font-bold text-brand-navy mt-3">{d.name}</div>
                <div className="text-sm text-slate-500">{d.location || "—"}</div>
                <div className="text-xs text-slate-400 mt-1">user: {d.username} · last: {d.last_activity ? new Date(d.last_activity).toLocaleString() : "never"}</div>
                <div className="flex gap-3 mt-4 text-xs font-medium">
                  {d.status !== "disabled" ? <button onClick={() => timeApi.deviceStatus(d.id, "disabled").then(loadDevices)} className="text-amber-600">Disable</button> : <button onClick={() => timeApi.deviceStatus(d.id, "offline").then(loadDevices)} className="text-emerald-600">Enable</button>}
                  <button onClick={() => timeApi.deleteDevice(d.id).then(loadDevices)} className="text-red-500">Delete</button>
                </div>
              </div>
            ))}
            {devices.length === 0 && <div className="text-slate-400 text-sm col-span-full py-8 text-center">No kiosks yet. Add one to get started.</div>}
          </div>
        </TabsContent>

        {/* Exceptions */}
        <TabsContent value="exceptions">
          <div className="flex items-center gap-2 mb-4"><Clock className="w-4 h-4 text-slate-400" /><input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="bg-white border border-slate-200 rounded-lg px-3 h-10 text-sm outline-none" /></div>
          <div className="bg-white rounded-2xl border border-slate-100 divide-y divide-slate-50">
            {exceptions.length === 0 && <div className="py-12 text-center text-slate-400"><CheckCircle2 className="w-8 h-8 mx-auto mb-2 text-emerald-400" />No exceptions for this date.</div>}
            {exceptions.map((e) => (
              <div key={e.id} className="flex items-center gap-3 px-5 py-3.5">
                <AlertTriangle className="w-4 h-4 text-amber-500 shrink-0" />
                <div className="flex-1"><div className="font-semibold text-brand-navy text-sm">{e.exception}</div><div className="text-xs text-slate-500">{e.employee_name} · {e.department}</div></div>
                <div className="text-xs text-slate-400">{e.authentication_method} · {(e.timestamp || "").slice(11, 16)}</div>
              </div>
            ))}
          </div>
        </TabsContent>
      </Tabs>

      {enrol && <EnrolDialog open={!!enrol} onOpenChange={(v) => !v && setEnrol(null)} employee={enrol} onDone={loadBios} />}
      {pinEmp && <PinDialog open={!!pinEmp} onOpenChange={(v) => !v && setPinEmp(null)} employee={pinEmp} onDone={loadBios} />}

      {/* Add device dialog */}
      <Dialog open={devDialog} onOpenChange={setDevDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader><DialogTitle className="font-display text-brand-navy">Add kiosk device</DialogTitle><DialogDescription>Create secure credentials for a workplace entrance kiosk.</DialogDescription></DialogHeader>
          <form onSubmit={createDevice} className="space-y-4">
            <div className="grid gap-1.5"><Label className="text-xs text-slate-500">Device name</Label><Input value={devForm.name} onChange={(e) => setDevForm({ ...devForm, name: e.target.value })} placeholder="Main Office Entrance" required /></div>
            <div className="grid gap-1.5"><Label className="text-xs text-slate-500">Location</Label><Input value={devForm.location} onChange={(e) => setDevForm({ ...devForm, location: e.target.value })} placeholder="Gaborone" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div className="grid gap-1.5"><Label className="text-xs text-slate-500">Username</Label><Input value={devForm.username} onChange={(e) => setDevForm({ ...devForm, username: e.target.value })} placeholder="kiosk-main" required /></div>
              <div className="grid gap-1.5"><Label className="text-xs text-slate-500">Password</Label><Input type="text" value={devForm.password} onChange={(e) => setDevForm({ ...devForm, password: e.target.value })} placeholder="Set a password" required /></div>
            </div>
            <Button type="submit" className="w-full bg-brand-blue hover:bg-brand-bluedark text-white rounded-lg h-11">Create kiosk</Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* new credentials shown once */}
      <Dialog open={!!newCred} onOpenChange={(v) => !v && setNewCred(null)}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader><DialogTitle className="font-display text-brand-navy">Kiosk created</DialogTitle><DialogDescription>Use these on the kiosk device sign-in.</DialogDescription></DialogHeader>
          <div className="bg-brand-mist rounded-xl p-4 text-sm space-y-1">
            <div><span className="text-slate-500">Org code:</span> <span className="font-mono font-semibold">{orgCode}</span></div>
            <div><span className="text-slate-500">Username:</span> <span className="font-mono font-semibold">{newCred?.username}</span></div>
            <div><span className="text-slate-500">Password:</span> <span className="font-mono font-semibold">{newCred?.password}</span></div>
          </div>
          <Button onClick={() => setNewCred(null)} className="w-full bg-brand-blue hover:bg-brand-bluedark text-white rounded-lg h-11 mt-2">Done</Button>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!confirm} onOpenChange={(v) => !v && setConfirm(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{confirm?.action === "delete" ? "Delete biometric?" : "Disable biometric?"}</AlertDialogTitle>
            <AlertDialogDescription>{confirm?.action === "delete" ? `This permanently deletes ${confirm?.emp?.name}'s face template.` : `${confirm?.emp?.name} won't be recognised until re-enabled.`} This is logged in the audit trail.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={() => bioAction(confirm.emp, confirm.action)} className="bg-destructive hover:bg-destructive/90">{confirm?.action === "delete" ? "Delete" : "Disable"}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
